import { AutomationRun, ReleaseStepType } from '../types';
import { RecursiveStringifyEnums } from './buildDistro';

export const iosStepAutomations: Record<ReleaseStepType, RecursiveStringifyEnums<AutomationRun[]>> = {
    ciDistribution: [],
    vcsDistribution: [],
    takeoff: [
        {
            name: 'Release app',
            autoScheduledFromCadence: true,
            description:
                'Runway will release your app update (or start your phased release, if applicable) on the scheduled date if all previous steps are ready (green).',

            id: 'AutoRelease',
            stepConfigId: null,
            type: 'AutoRelease',
            isLifecycle: true,
            isPartOfSequence: false,
            scheduledAt: '2023-06-19T07:00:00Z',
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Tag releases at the end of the release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will tag your production release commit at the end of each release cycle, and generate a summary of all work going out with the release.',

            id: 'AutoTagReleases',
            stepConfigId: null,
            type: 'AutoTagReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Attach build artifacts to VCS release record when tagging',
            autoScheduledFromCadence: null,
            description:
                'When tagging your production release commit, Runway will attach all artifacts generated from your active production build workflow as assets on your release record.',

            id: 'AutoUploadReleaseAssets',
            stepConfigId: null,
            type: 'AutoUploadReleaseAssets',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Mark releases as completed in project management tool',
            autoScheduledFromCadence: null,
            description:
                'Runway will mark releases as completed in your project management tool at the end of each release cycle.',

            id: 'AutoCompleteIssueTrackingReleases',
            stepConfigId: null,
            type: 'AutoCompleteIssueTrackingReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Halt unstable phased releases',
            autoScheduledFromCadence: null,
            description:
                'Runway will pause your phased release if all of your defined conditions have been met, and any of your selected health metrics become unhealthy.',

            id: 'AutoHaltUnstablePhasedReleases',
            stepConfigId: null,
            type: 'AutoHaltUnstablePhasedReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Backmerge changes from the release branch',
            autoScheduledFromCadence: null,
            description:
                'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',

            id: 'AutoBackmergeReleaseBranches',
            stepConfigId: null,
            type: 'AutoBackmergeReleaseBranches',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Release stable phased releases to 100% of users',
            autoScheduledFromCadence: null,
            description:
                'Runway will release your update to all users early if all of your defined conditions have been met, and your selected health metrics stay within your defined health threshold.',

            id: 'AutoPromoteStablePhasedReleases',
            stepConfigId: null,
            type: 'AutoPromoteStablePhasedReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Delete version-specific release branches at the end of the release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will delete version-specific release branches when the release is complete and tagged.',

            id: 'AutoDeleteReleaseBranch',
            stepConfigId: null,
            type: 'AutoDeleteReleaseBranch',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Update status of tickets upon milestone',
            autoScheduledFromCadence: null,
            description:
                'Runway will update tickets associated with the release to the configured status when each configured release milestone is reached.',
            id: 'AutoTransitionIssueTrackingTickets',
            stepConfigId: null,
            type: 'AutoTransitionIssueTrackingTickets',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Distribute release summary to additional recipients at the end of each release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will distribute a release summary via email to additional recipients at the end of each release cycle. You can configure the release summary template from App settings > Custom strings, and you can make changes to the release summary for a specific release from the Release step up until the release has gone live. By default, release summaries will be sent to all app users.',
            id: 'AutoDistributeReleaseSummary',
            stepConfigId: null,
            type: 'AutoDistributeReleaseSummary',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Prepare rollback releases',
            autoScheduledFromCadence: null,
            description:
                'Runway will re-sign, upload and (optionally) submit the previous stable build to have ready in case your latest release needs to be pulled back.',
            id: 'AutoPrepareRollbackRelease',
            stepConfigId: null,
            type: 'AutoPrepareRollbackRelease',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Custom phased / staged rollout',
            autoScheduledFromCadence: null,
            description:
                'Customize phased rollouts with blackout dates and times – Runway will pause and resume phased rollouts to avoid phased rollout percentage increments on the blackout dates and times you specify.',
            id: 'AutoStagedRollout',
            stepConfigId: null,
            type: 'AutoStagedRollout',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    screenshots: [],
    storeReview: [],
    kickoff: [
        {
            name: 'Bump version',
            autoScheduledFromCadence: null,
            description:
                'Runway will bump the version on your working branch or release branch according to the strategy set for the app.',

            id: 'AutoBumpVersion',
            stepConfigId: null,
            type: 'AutoBumpVersion',
            isLifecycle: false,

            isPartOfSequence: true,

            scheduledAt: '2023-06-09T21:00:00Z',
            sequenceNumber: 1,
            status: 'success',
            statusMessage: 'PR #10691',
            statusMessageLinkUrl: 'https://github.com',
            relevantPRs: [],
        },
        {
            name: 'Kick off release on target date',
            autoScheduledFromCadence: true,
            description:
                'Runway will create a release branch off of your working branch for the next release on the scheduled date.',

            id: 'AutoKickoff',
            stepConfigId: null,
            type: 'AutoKickoff',
            isLifecycle: true,

            isPartOfSequence: true,

            scheduledAt: '2023-06-09T21:00:00Z',
            sequenceNumber: 0,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Trigger workflow after release kickoff',
            autoScheduledFromCadence: null,
            description: 'Runway will trigger the configured CI build workflow after release is kicked off.',

            id: 'AutoKickoffTriggerWorkflow',
            stepConfigId: null,
            type: 'AutoKickoffTriggerWorkflow',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create and sync App Store Connect versions as needed to match current Runway release',
            autoScheduledFromCadence: null,
            description:
                'Runway will create a new version of your app in App Store Connect once the next release is being prepared in Runway.',

            id: 'AutoCreateAppStoreVersions',
            stepConfigId: null,
            type: 'AutoCreateAppStoreVersions',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create upcoming version in Runway when current release is kicked off',
            autoScheduledFromCadence: null,
            description:
                'Runway will create a new release record for your next version when your current release is kicked off. This will assign a release pilot and provide a selectable upcoming release via the versions timeline in App Home.',

            id: 'AutoCreateRunwayReleases',
            stepConfigId: null,
            type: 'AutoCreateRunwayReleases',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Merge pull requests opened by Runway',
            autoScheduledFromCadence: null,
            description:
                'Runway creates pull requests to fulfill certain tasks (e.g. bumping version in code). If enabled, Runway will attempt to merge these PRs automatically, once any status checks or approvals are satisfied.',

            id: 'AutoMergeRunwayPRs',
            stepConfigId: null,
            type: 'AutoMergeRunwayPRs',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create release-specific Slack or Microsoft Teams channel(s)',
            autoScheduledFromCadence: null,
            description:
                "Runway will automatically create new Slack channel(s) for each release. If a tokenized channel name (e.g. release-{version}) is configured as either the 'Primary channel' for the Slack integration, or as a channel override for any notification in settings, Runway will create a new channel matching the tokenized channel name for each active release if needed.",

            id: 'AutoCreateReleaseChannels',
            stepConfigId: null,
            type: 'AutoCreateReleaseChannels',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create regression test runs',
            autoScheduledFromCadence: null,
            description:
                "Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.",
            id: 'AutoCreateTestRuns',
            stepConfigId: null,
            type: 'AutoCreateTestRuns',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    featureReadiness: [
        {
            name: 'Add appropriate labels and/or fix versions when they are missing from tickets',
            autoScheduledFromCadence: null,
            description:
                'Runway will add appropriate labels and/or fix versions when they are missing from tickets that are associated with the release. These label and fix version patterns are defined in App Settings.',

            id: 'AutoAddMissingFAs',
            stepConfigId: null,
            type: 'AutoAddMissingFAs',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Pull (cherry-pick) fixes into the release',
            autoScheduledFromCadence: null,
            description:
                'For any merged PR whose title contains the text pattern specified below, Runway will cherry-pick its commits from the working branch to the relevant release branch. Note that Runway will always open a PR containing the cherry-picked commits and targeting the release branch.',

            id: 'AutoCherryPickFromWorkingBranch',
            stepConfigId: null,
            type: 'AutoCherryPickFromWorkingBranch',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Backmerge changes from the release branch',
            autoScheduledFromCadence: null,
            description:
                'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',

            id: 'AutoBackmergeReleaseBranches',
            stepConfigId: null,
            type: 'AutoBackmergeReleaseBranches',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Post build info to project management tickets',
            autoScheduledFromCadence: null,
            description:
                'Runway will post comments on your project management tickets with build info for builds that contain associated code.',
            id: 'AutoPostBuildInfoOnPMTickets',
            stepConfigId: null,
            type: 'AutoPostBuildInfoOnPMTickets',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    regressionTesting: [
        {
            name: 'Create regression test runs',
            autoScheduledFromCadence: null,
            description:
                "Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.",
            id: 'AutoCreateTestRuns',
            stepConfigId: null,
            type: 'AutoCreateTestRuns',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    betaTesting: [
        {
            name: 'Apply beta testing notes to new beta builds',
            autoScheduledFromCadence: null,
            description: 'Runway will apply beta testing notes to new beta builds as they become available.',

            id: 'AutoApplyBetaBuildNotes',
            stepConfigId: null,
            type: 'AutoApplyBetaBuildNotes',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            description:
                'Runway will submit any new builds uploaded to TestFlight for beta review as soon as they finish processing.',
            id: 'AutoSubmitBetaBuilds',
            stepConfigId: null,
            type: 'AutoSubmitBetaBuilds',
            name: 'Submit new builds for beta review',
            status: 'active',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            description:
                'Runway will assign any new builds uploaded to TestFlight to your default beta groups for testing. External testers will have access once the build has been reviewed.',
            id: 'AutoAssignBetaBuilds',
            stepConfigId: null,
            type: 'AutoAssignBetaBuilds',
            name: 'Assign beta testing builds to default testing groups',
            status: 'active',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    submission: [
        {
            name: 'Submit app for review',
            autoScheduledFromCadence: true,
            description: 'Runway will submit your selected build for review if all previous steps are ready (green).',

            id: 'AutoSubmit',
            stepConfigId: null,
            type: 'AutoSubmit',
            isLifecycle: true,

            isPartOfSequence: false,

            scheduledAt: '2023-06-16T16:00:00Z',
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Select build for App Store Connect',
            autoScheduledFromCadence: null,
            description:
                'Based on your selection below, Runway will select a build for the release version, and select it for release in App Store Connect. Note that there may be a processing delay of a few minutes.',

            id: 'AutoSelectLatestBuild',
            stepConfigId: null,
            type: 'AutoSelectLatestBuild',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },

        {
            name: 'Upload build artifacts for distribution',
            autoScheduledFromCadence: null,
            description: 'Runway will upload build artifacts for distribution according to the configured rules.',

            id: 'AutoUploadBinaryArtifactToAppStore',
            stepConfigId: null,
            type: 'AutoUploadBinaryArtifactToAppStore',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Upload dSYMs to stability monitoring',
            autoScheduledFromCadence: null,
            description:
                'Runway will automatically upload dSYM files from build artifacts to your stability monitoring platform.',

            id: 'AutoUploadDsymsToStabilityMonitoring',
            stepConfigId: null,
            type: 'AutoUploadDsymsToStabilityMonitoring',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Apply default review attachment to new app versions in App Store Connect',
            autoScheduledFromCadence: null,
            description:
                'When submitting a new release for review, Runway will apply your default file attachment provided for reviewers (as defined in App Settings) to new versions created in App Store Connect.',

            id: 'AutoApplyReviewAttachments',
            stepConfigId: null,
            type: 'AutoApplyReviewAttachments',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    metadata: [
        {
            name: 'Populate store metadata to new releases',
            autoScheduledFromCadence: null,
            description:
                "Runway will apply 'What's New' text to new versions created in App Store Connect. You can make updates to the text as needed before you submit your update for review.",

            id: 'AutoApplyMetadata',
            stepConfigId: null,
            type: 'AutoApplyMetadata',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Sync release metadata translations',
            id: 'AutoSyncMetadataTranslations',
            stepConfigId: null,
            type: 'AutoSyncMetadataTranslations',
            description:
                'Runway will upload release metadata for translation if needed and export translated metadata to App Store Connect when ready.',
            scheduledAt: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,
            hasEditableSettings: true,
            relevantPRs: [],
        },
        {
            name: 'Sync release metadata from version control',
            id: 'AutoSyncMetadataFromVCS',
            stepConfigId: null,
            type: 'AutoSyncMetadataFromVCS',
            description:
                'Runway will read release metadata from files in your repo and automatically sync metadata to your release in App Store Connect.',
            scheduledAt: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,
            hasEditableSettings: true,
            relevantPRs: [],
        },
    ],
    translations: [
        {
            name: 'Update translations on the release branch from the working branch',
            id: 'AutoCherryPickLocalizableStrings',
            stepConfigId: null,
            type: 'AutoCherryPickLocalizableStrings',
            description:
                'Runway will update translations on the release branch by pulling over new translations from the working branch as needed.',

            scheduledAt: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,

            hasEditableSettings: true,
            relevantPRs: [],
        },
        {
            name: 'Sync localizable string translations',
            id: 'AutoSyncTranslations',
            stepConfigId: null,
            type: 'AutoSyncTranslations',
            description:
                'Runway will upload new or updated strings for translation as needed, and export strings from the provider and open a pull request when ready.',

            scheduledAt: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,

            hasEditableSettings: true,
            relevantPRs: [],
        },
    ],
    releaseCandidate: [
        {
            name: 'Enable artifact downloads',
            autoScheduledFromCadence: null,
            description:
                'Runway will download all artifacts generated from your build workflow and provide direct download links to them from the Runway interface. Additionally, Runway will include artifact information in Slack build success notifications, including a link to find those downloads in Runway.',

            id: 'AutoDownloadArtifacts',
            stepConfigId: null,
            type: 'AutoDownloadArtifacts',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Upload build artifacts for distribution',
            autoScheduledFromCadence: null,
            description: 'Runway will upload build artifacts for distribution according to the configured rules.',

            id: 'AutoUploadBinaryArtifactToAppStore',
            stepConfigId: null,
            type: 'AutoUploadBinaryArtifactToAppStore',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Upload dSYMs to stability monitoring',
            autoScheduledFromCadence: null,
            description:
                'Runway will automatically upload dSYM files from build artifacts to your stability monitoring platform.',

            id: 'AutoUploadDsymsToStabilityMonitoring',
            stepConfigId: null,
            type: 'AutoUploadDsymsToStabilityMonitoring',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    approvals: [],
    rollbackBuild: [],
    rollout: [],
    releaseCycle: [],
};

export const androidStepAutomations: Record<ReleaseStepType, RecursiveStringifyEnums<AutomationRun[]>> = {
    screenshots: [],
    ciDistribution: [],
    vcsDistribution: [],
    submission: [
        {
            name: 'Submit app for review',
            autoScheduledFromCadence: false,
            description: 'Runway will submit your selected build for review if all previous steps are ready (green).',

            id: 'AutoSubmit',
            stepConfigId: null,
            type: 'AutoSubmit',
            isLifecycle: true,

            isPartOfSequence: false,

            scheduledAt: '2023-06-18T21:00:00Z',
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Add APK/AAB to the production track',
            autoScheduledFromCadence: null,
            description:
                'Based on your selection below, Runway will select an APK/AAB in the Play Console, and apply it to the Draft release on the production track. You can optionally choose to limit automatic selection to APKs/AABs that are already available on a specific testing track.',

            id: 'AutoSelectLatestBuild',
            stepConfigId: null,
            type: 'AutoSelectLatestBuild',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Upload build artifacts for distribution',
            autoScheduledFromCadence: null,
            description: 'Runway will upload build artifacts for distribution according to the configured rules.',

            id: 'AutoUploadBinaryArtifactToAppStore',
            stepConfigId: null,
            type: 'AutoUploadBinaryArtifactToAppStore',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    releaseCandidate: [
        {
            name: 'Enable artifact downloads',
            autoScheduledFromCadence: null,
            description:
                'Runway will download all artifacts generated from your build workflow and provide direct download links to them from the Runway interface. Additionally, Runway will include artifact information in Slack build success notifications, including a link to find those downloads in Runway.',

            id: 'AutoDownloadArtifacts',
            stepConfigId: null,
            type: 'AutoDownloadArtifacts',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Upload build artifacts for distribution',
            autoScheduledFromCadence: null,
            description: 'Runway will upload build artifacts for distribution according to the configured rules.',

            id: 'AutoUploadBinaryArtifactToAppStore',
            stepConfigId: null,
            type: 'AutoUploadBinaryArtifactToAppStore',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    betaTesting: [
        {
            name: 'Promote builds to testing track',
            autoScheduledFromCadence: null,
            description:
                'Runway will promote any newly uploaded APKs/AABs to your testing track and start the rollout.',

            id: 'AutoPromoteBuildsToTestTrack',
            stepConfigId: null,
            type: 'AutoPromoteBuildsToTestTrack',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Apply beta testing notes to new beta builds',
            autoScheduledFromCadence: null,
            description: 'Runway will apply beta testing notes to new beta builds as they become available.',

            id: 'AutoApplyBetaBuildNotes',
            stepConfigId: null,
            type: 'AutoApplyBetaBuildNotes',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            id: 'AutoAssignBetaBuilds',
            type: 'AutoAssignBetaBuilds',
            name: 'Assign beta testing builds to default testing groups',
            stepConfigId: null,
            description: 'Runway will assign any new distributed builds to your default beta groups for testing.',
            scheduledAt: null,
            status: 'inactive',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,
            hasEditableSettings: true,
            relevantPRs: [],
        },
    ],
    metadata: [
        {
            name: 'Populate store metadata to new releases',
            autoScheduledFromCadence: null,
            description:
                'Runway will apply release notes to new versions created in the Play Console. You can make updates to the text as needed before you submit your update for review.',

            id: 'AutoApplyMetadata',
            stepConfigId: null,
            type: 'AutoApplyMetadata',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Sync metadata translations',
            id: 'AutoSyncMetadataTranslations',
            stepConfigId: null,
            type: 'AutoSyncMetadataTranslations',
            description:
                'Runway will upload release metadata for translation if needed and export translated metadata to Play Console when ready.',

            scheduledAt: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,

            hasEditableSettings: true,
            relevantPRs: [],
        },
        {
            name: 'Sync release metadata from version control',
            id: 'AutoSyncMetadataFromVCS',
            stepConfigId: null,
            type: 'AutoSyncMetadataFromVCS',
            description:
                'Runway will read release metadata from files in your repo and automatically sync metadata to your release in Play Console.',

            scheduledAt: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,
            hasEditableSettings: true,
            relevantPRs: [],
        },
    ],
    translations: [
        {
            name: 'Update translations on the release branch from the working branch',
            id: 'AutoCherryPickLocalizableStrings',
            stepConfigId: null,
            type: 'AutoCherryPickLocalizableStrings',
            description:
                'Runway will update translations on the release branch by pulling over new translations from the working branch as needed.',

            scheduledAt: null,
            status: 'inactive',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,

            hasEditableSettings: true,
            relevantPRs: [],
        },
        {
            name: 'Sync metadata translations',
            id: 'AutoSyncMetadataTranslations',
            stepConfigId: null,
            type: 'AutoSyncMetadataTranslations',
            description:
                'Runway will upload release metadata for translation if needed and export translated metadata to App Store Connect when ready.',

            scheduledAt: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,

            hasEditableSettings: true,
            relevantPRs: [],
        },
        {
            name: 'Sync localizable string translations',
            id: 'AutoSyncTranslations',
            stepConfigId: null,
            type: 'AutoSyncTranslations',
            description:
                'Runway will upload new or updated strings for translation as needed, and export strings from the provider and open a pull request when ready.',

            scheduledAt: null,
            status: 'inactive',
            statusMessage: null,
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: null,
            isLifecycle: false,
            autoScheduledFromCadence: null,

            hasEditableSettings: true,
            relevantPRs: [],
        },
    ],
    storeReview: [],
    takeoff: [
        {
            name: 'Release app',
            autoScheduledFromCadence: true,
            description:
                'Runway will release your app update (or start your phased release, if applicable) on the scheduled date if all previous steps are ready (green).',

            id: 'AutoRelease',
            stepConfigId: null,
            type: 'AutoRelease',
            isLifecycle: true,
            isPartOfSequence: false,
            scheduledAt: '2023-06-19T07:00:00Z',
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Tag releases at the end of the release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will tag your production release commit at the end of each release cycle, and generate a summary of all work going out with the release.',

            id: 'AutoTagReleases',
            stepConfigId: null,
            type: 'AutoTagReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Attach build artifacts to VCS release record when tagging',
            autoScheduledFromCadence: null,
            description:
                'When tagging your production release commit, Runway will attach all artifacts generated from your active production build workflow as assets on your release record.',

            id: 'AutoUploadReleaseAssets',
            stepConfigId: null,
            type: 'AutoUploadReleaseAssets',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Mark releases as completed in project management tool',
            autoScheduledFromCadence: null,
            description:
                'Runway will mark releases as completed in your project management tool at the end of each release cycle.',

            id: 'AutoCompleteIssueTrackingReleases',
            stepConfigId: null,
            type: 'AutoCompleteIssueTrackingReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Halt unstable phased releases',
            autoScheduledFromCadence: null,
            description:
                'Runway will pause your phased release if all of your defined conditions have been met, and any of your selected health metrics become unhealthy.',

            id: 'AutoHaltUnstablePhasedReleases',
            stepConfigId: null,
            type: 'AutoHaltUnstablePhasedReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Backmerge changes from the release branch',
            autoScheduledFromCadence: null,
            description:
                'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',

            id: 'AutoBackmergeReleaseBranches',
            stepConfigId: null,
            type: 'AutoBackmergeReleaseBranches',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Release stable phased releases to 100% of users',
            autoScheduledFromCadence: null,
            description:
                'Runway will release your update to all users early if all of your defined conditions have been met, and your selected health metrics stay within your defined health threshold',

            id: 'AutoPromoteStablePhasedReleases',
            stepConfigId: null,
            type: 'AutoPromoteStablePhasedReleases',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Delete version-specific release branches at the end of the release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will delete version-specific release branches when the release is complete and tagged.',

            id: 'AutoDeleteReleaseBranch',
            stepConfigId: null,
            type: 'AutoDeleteReleaseBranch',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Delete version-specific release branches at the end of the release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will delete version-specific release branches when the release is complete and tagged.',

            id: 'AutoDeleteReleaseBranch',
            stepConfigId: null,
            type: 'AutoDeleteReleaseBranch',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Update status of tickets upon milestone',
            autoScheduledFromCadence: null,
            description:
                'Runway will update tickets associated with the release to the configured status when each configured release milestone is reached.',
            id: 'AutoTransitionIssueTrackingTickets',
            stepConfigId: null,
            type: 'AutoTransitionIssueTrackingTickets',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Distribute release summary to additional recipients at the end of each release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will distribute a release summary via email to additional recipients at the end of each release cycle. You can configure the release summary template from App settings > Custom strings, and you can make changes to the release summary for a specific release from the Release step up until the release has gone live. By default, release summaries will be sent to all app users.',
            id: 'AutoDistributeReleaseSummary',
            stepConfigId: null,
            type: 'AutoDistributeReleaseSummary',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Prepare rollback releases',
            autoScheduledFromCadence: null,
            description:
                'Runway will re-sign, upload and (optionally) submit the previous stable build to have ready in case your latest release needs to be pulled back.',
            id: 'AutoPrepareRollbackRelease',
            stepConfigId: null,
            type: 'AutoPrepareRollbackRelease',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Custom phased / staged rollout',
            autoScheduledFromCadence: null,
            description: 'Runway will increase the staged rollout percentage over the course of several days.',
            id: 'AutoStagedRollout',
            stepConfigId: null,
            type: 'AutoStagedRollout',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    kickoff: [
        {
            name: 'Bump version',
            autoScheduledFromCadence: null,
            description:
                'Runway will bump the version on your working branch or release branch according to the strategy set for the app.',

            id: 'AutoBumpVersion',
            stepConfigId: null,
            type: 'AutoBumpVersion',
            isLifecycle: false,

            isPartOfSequence: true,

            scheduledAt: '2023-06-09T21:00:00Z',
            sequenceNumber: 1,
            status: 'success',
            statusMessage: 'PR #10691',
            statusMessageLinkUrl: 'https://github.com',
            relevantPRs: [],
        },
        {
            name: 'Kick off release on target date',
            autoScheduledFromCadence: true,
            description:
                'Runway will create a release branch off of your working branch for the next release on the scheduled date.',

            id: 'AutoKickoff',
            stepConfigId: null,
            type: 'AutoKickoff',
            isLifecycle: true,

            isPartOfSequence: true,

            scheduledAt: '2023-06-09T21:00:00Z',
            sequenceNumber: 0,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Trigger workflow after release kickoff',
            autoScheduledFromCadence: null,
            description: 'Runway will trigger the configured CI build workflow after release is kicked off.',

            id: 'AutoKickoffTriggerWorkflow',
            stepConfigId: null,
            type: 'AutoKickoffTriggerWorkflow',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create and sync App Store Connect versions as needed to match current Runway release',
            autoScheduledFromCadence: null,
            description:
                'Runway will create a new version of your app in App Store Connect once the next release is being prepared in Runway.',

            id: 'AutoCreateAppStoreVersions',
            stepConfigId: null,
            type: 'AutoCreateAppStoreVersions',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create upcoming version in Runway when current release is kicked off',
            autoScheduledFromCadence: null,
            description:
                'Runway will create a new release record for your next version when your current release is kicked off. This will assign a release pilot and provide a selectable upcoming release via the versions timeline in App Home.',

            id: 'AutoCreateRunwayReleases',
            stepConfigId: null,
            type: 'AutoCreateRunwayReleases',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Merge pull requests opened by Runway',
            autoScheduledFromCadence: null,
            description:
                'Runway creates pull requests to fulfill certain tasks (e.g. bumping version in code). If enabled, Runway will attempt to merge these PRs automatically, once any status checks or approvals are satisfied.',

            id: 'AutoMergeRunwayPRs',
            stepConfigId: null,
            type: 'AutoMergeRunwayPRs',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create release-specific Slack or Microsoft Teams channel(s)',
            autoScheduledFromCadence: null,
            description:
                "Runway will automatically create new Slack channel(s) for each release. If a tokenized channel name (e.g. release-{version}) is configured as either the 'Primary channel' for the Slack integration, or as a channel override for any notification in settings, Runway will create a new channel matching the tokenized channel name for each active release if needed.",

            id: 'AutoCreateReleaseChannels',
            stepConfigId: null,
            type: 'AutoCreateReleaseChannels',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create regression test runs',
            autoScheduledFromCadence: null,
            description:
                "Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.",
            id: 'AutoCreateTestRuns',
            stepConfigId: null,
            type: 'AutoCreateTestRuns',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    regressionTesting: [
        {
            name: 'Create regression test runs',
            autoScheduledFromCadence: null,
            description:
                "Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.",
            id: 'AutoCreateTestRuns',
            stepConfigId: null,
            type: 'AutoCreateTestRuns',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    featureReadiness: [
        {
            name: 'Add appropriate labels and/or fix versions when they are missing from tickets',
            autoScheduledFromCadence: null,
            description:
                'Runway will add appropriate labels and/or fix versions when they are missing from tickets that are associated with the release. These label and fix version patterns are defined in App Settings.',

            id: 'AutoAddMissingFAs',
            stepConfigId: null,
            type: 'AutoAddMissingFAs',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Pull (cherry-pick) fixes into the release',
            autoScheduledFromCadence: null,
            description:
                'For any merged PR whose title contains the text pattern specified below, Runway will cherry-pick its commits from the working branch to the relevant release branch. Note that Runway will always open a PR containing the cherry-picked commits and targeting the release branch.',

            id: 'AutoCherryPickFromWorkingBranch',
            stepConfigId: null,
            type: 'AutoCherryPickFromWorkingBranch',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Backmerge changes from the release branch',
            autoScheduledFromCadence: null,
            description:
                'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',

            id: 'AutoBackmergeReleaseBranches',
            stepConfigId: null,
            type: 'AutoBackmergeReleaseBranches',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Post build info to project management tickets',
            autoScheduledFromCadence: null,
            description:
                'Runway will post comments on your project management tickets with build info for builds that contain associated code.',
            id: 'AutoPostBuildInfoOnPMTickets',
            stepConfigId: null,
            type: 'AutoPostBuildInfoOnPMTickets',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    approvals: [],
    rollbackBuild: [],
    rollout: [],
    releaseCycle: [],
};

export const otaStepAutomations: Record<ReleaseStepType, RecursiveStringifyEnums<AutomationRun[]>> = {
    ciDistribution: [],
    vcsDistribution: [],
    takeoff: [
        {
            name: 'Release app',
            autoScheduledFromCadence: true,
            description:
                'Runway will release your app update (or start your phased release, if applicable) on the scheduled date if all previous steps are ready (green).',

            id: 'AutoRelease',
            stepConfigId: null,
            type: 'AutoRelease',
            isLifecycle: true,

            isPartOfSequence: false,

            scheduledAt: '2023-06-19T07:00:00Z',
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Tag releases at the end of the release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will tag your production release commit at the end of each release cycle, and generate a summary of all work going out with the release.',

            id: 'AutoTagReleases',
            stepConfigId: null,
            type: 'AutoTagReleases',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Attach build artifacts to VCS release record when tagging',
            autoScheduledFromCadence: null,
            description:
                'When tagging your production release commit, Runway will attach all artifacts generated from your active production build workflow as assets on your release record.',

            id: 'AutoUploadReleaseAssets',
            stepConfigId: null,
            type: 'AutoUploadReleaseAssets',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Mark releases as completed in project management tool',
            autoScheduledFromCadence: null,
            description:
                'Runway will mark releases as completed in your project management tool at the end of each release cycle.',

            id: 'AutoCompleteIssueTrackingReleases',
            stepConfigId: null,
            type: 'AutoCompleteIssueTrackingReleases',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Halt unstable phased releases',
            autoScheduledFromCadence: null,
            description:
                'Runway will pause your phased release if all of your defined conditions have been met, and any of your selected health metrics become unhealthy.',

            id: 'AutoHaltUnstablePhasedReleases',
            stepConfigId: null,
            type: 'AutoHaltUnstablePhasedReleases',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Backmerge changes from the release branch',
            autoScheduledFromCadence: null,
            description:
                'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',

            id: 'AutoBackmergeReleaseBranches',
            stepConfigId: null,
            type: 'AutoBackmergeReleaseBranches',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Release stable phased releases to 100% of users',
            autoScheduledFromCadence: null,
            description:
                'Runway will release your update to all users early if all of your defined conditions have been met, and your selected health metrics stay within your defined health threshold.',

            id: 'AutoPromoteStablePhasedReleases',
            stepConfigId: null,
            type: 'AutoPromoteStablePhasedReleases',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Delete version-specific release branches at the end of the release cycle',
            autoScheduledFromCadence: null,
            description:
                'Runway will delete version-specific release branches when the release is complete and tagged.',

            id: 'AutoDeleteReleaseBranch',
            stepConfigId: null,
            type: 'AutoDeleteReleaseBranch',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    screenshots: [],
    storeReview: [],
    kickoff: [
        {
            name: 'Bump version',
            autoScheduledFromCadence: null,
            description:
                'Runway will bump the version on your working branch or release branch according to the strategy set for the app.',

            id: 'AutoBumpVersion',
            stepConfigId: null,
            type: 'AutoBumpVersion',
            isLifecycle: false,

            isPartOfSequence: true,

            scheduledAt: '2023-06-09T21:00:00Z',
            sequenceNumber: 1,
            status: 'success',
            statusMessage: 'PR #10691',
            statusMessageLinkUrl: 'https://github.com',
            relevantPRs: [],
        },
        {
            name: 'Kick off release on target date',
            autoScheduledFromCadence: true,
            description:
                'Runway will create a release branch off of your working branch for the next release on the scheduled date.',

            id: 'AutoKickoff',
            stepConfigId: null,
            type: 'AutoKickoff',
            isLifecycle: true,

            isPartOfSequence: true,

            scheduledAt: '2023-06-09T21:00:00Z',
            sequenceNumber: 0,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Trigger workflow after release kickoff',
            autoScheduledFromCadence: null,
            description: 'Runway will trigger the configured CI build workflow after release is kicked off.',

            id: 'AutoKickoffTriggerWorkflow',
            stepConfigId: null,
            type: 'AutoKickoffTriggerWorkflow',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create and sync App Store Connect versions as needed to match current Runway release',
            autoScheduledFromCadence: null,
            description:
                'Runway will create a new version of your app in App Store Connect once the next release is being prepared in Runway.',

            id: 'AutoCreateAppStoreVersions',
            stepConfigId: null,
            type: 'AutoCreateAppStoreVersions',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create upcoming version in Runway when current release is kicked off',
            autoScheduledFromCadence: null,
            description:
                'Runway will create a new release record for your next version when your current release is kicked off. This will assign a release pilot and provide a selectable upcoming release via the versions timeline in App Home.',

            id: 'AutoCreateRunwayReleases',
            stepConfigId: null,
            type: 'AutoCreateRunwayReleases',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Merge pull requests opened by Runway',
            autoScheduledFromCadence: null,
            description:
                'Runway creates pull requests to fulfill certain tasks (e.g. bumping version in code). If enabled, Runway will attempt to merge these PRs automatically, once any status checks or approvals are satisfied.',

            id: 'AutoMergeRunwayPRs',
            stepConfigId: null,
            type: 'AutoMergeRunwayPRs',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create release-specific Slack or Microsoft Teams channel(s)',
            autoScheduledFromCadence: null,
            description:
                "Runway will automatically create new Slack channel(s) for each release. If a tokenized channel name (e.g. release-{version}) is configured as either the 'Primary channel' for the Slack integration, or as a channel override for any notification in settings, Runway will create a new channel matching the tokenized channel name for each active release if needed.",

            id: 'AutoCreateReleaseChannels',
            stepConfigId: null,
            type: 'AutoCreateReleaseChannels',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Create regression test runs',
            autoScheduledFromCadence: null,
            description:
                "Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.",
            id: 'AutoCreateTestRuns',
            stepConfigId: null,
            type: 'AutoCreateTestRuns',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    featureReadiness: [
        {
            name: 'Add appropriate labels and/or fix versions when they are missing from tickets',
            autoScheduledFromCadence: null,
            description:
                'Runway will add appropriate labels and/or fix versions when they are missing from tickets that are associated with the release. These label and fix version patterns are defined in App Settings.',

            id: 'AutoAddMissingFAs',
            stepConfigId: null,
            type: 'AutoAddMissingFAs',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Pull (cherry-pick) fixes into the release',
            autoScheduledFromCadence: null,
            description:
                'For any merged PR whose title contains the text pattern specified below, Runway will cherry-pick its commits from the working branch to the relevant release branch. Note that Runway will always open a PR containing the cherry-picked commits and targeting the release branch.',

            id: 'AutoCherryPickFromWorkingBranch',
            stepConfigId: null,
            type: 'AutoCherryPickFromWorkingBranch',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Backmerge changes from the release branch',
            autoScheduledFromCadence: null,
            description:
                'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',

            id: 'AutoBackmergeReleaseBranches',
            stepConfigId: null,
            type: 'AutoBackmergeReleaseBranches',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Post build info to project management tickets',
            autoScheduledFromCadence: null,
            description:
                'Runway will post comments on your project management tickets with build info for builds that contain associated code.',
            id: 'AutoPostBuildInfoOnPMTickets',
            stepConfigId: null,
            type: 'AutoPostBuildInfoOnPMTickets',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    regressionTesting: [
        {
            name: 'Create regression test runs',
            autoScheduledFromCadence: null,
            description:
                "Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.",
            id: 'AutoCreateTestRuns',
            stepConfigId: null,
            type: 'AutoCreateTestRuns',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    betaTesting: [],
    submission: [
        {
            name: 'Submit app for review',
            autoScheduledFromCadence: true,
            description: 'Runway will submit your selected build for review if all previous steps are ready (green).',

            id: 'AutoSubmit',
            stepConfigId: null,
            type: 'AutoSubmit',
            isLifecycle: true,

            isPartOfSequence: false,

            scheduledAt: '2023-06-16T16:00:00Z',
            sequenceNumber: null,
            status: 'success',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Select build for App Store Connect',
            autoScheduledFromCadence: null,
            description:
                'Based on your selection below, Runway will select a build for the release version, and select it for release in App Store Connect. Note that there may be a processing delay of a few minutes.',

            id: 'AutoSelectLatestBuild',
            stepConfigId: null,
            type: 'AutoSelectLatestBuild',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },

        {
            name: 'Upload build artifacts for distribution',
            autoScheduledFromCadence: null,
            description: 'Runway will upload build artifacts for distribution according to the configured rules.',

            id: 'AutoUploadBinaryArtifactToAppStore',
            stepConfigId: null,
            type: 'AutoUploadBinaryArtifactToAppStore',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
        {
            name: 'Upload dSYMs to stability monitoring',
            autoScheduledFromCadence: null,
            description:
                'Runway will automatically upload dSYM files from build artifacts to your stability monitoring platform.',

            id: 'AutoUploadDsymsToStabilityMonitoring',
            stepConfigId: null,
            type: 'AutoUploadDsymsToStabilityMonitoring',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    metadata: [
        {
            name: 'Populate store metadata to new releases',
            autoScheduledFromCadence: null,
            description:
                "Runway will apply 'What's New' text to new versions created in App Store Connect. You can make updates to the text as needed before you submit your update for review.",

            id: 'AutoApplyMetadata',
            stepConfigId: null,
            type: 'AutoApplyMetadata',
            isLifecycle: false,

            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    translations: [],
    releaseCandidate: [],
    approvals: [],
    rollbackBuild: [],
    rollout: [],
    releaseCycle: [],
};

export const workflowOnlyStepAutomations: Record<ReleaseStepType, RecursiveStringifyEnums<AutomationRun[]>> = {
    ciDistribution: [],
    vcsDistribution: [],
    takeoff: [],
    screenshots: [],
    storeReview: [],
    kickoff: [],
    featureReadiness: [],
    regressionTesting: [],
    betaTesting: [],
    submission: [],
    metadata: [],
    translations: [],
    releaseCandidate: [
        {
            name: 'Trigger CI workflow',
            autoScheduledFromCadence: null,
            description:
                'Runway will trigger the configured CI workflow based on the configured settings relative to kickoff, submission, or release.',
            id: 'AutoTriggerCIWorkflow',
            stepConfigId: null,
            type: 'AutoTriggerCIWorkflow',
            isLifecycle: false,
            isPartOfSequence: false,
            scheduledAt: null,
            sequenceNumber: null,
            status: 'active',
            statusMessage: null,
            statusMessageLinkUrl: null,
            relevantPRs: [],
        },
    ],
    approvals: [],
    rollbackBuild: [],
    rollout: [],
    releaseCycle: [],
};
